import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import AppPaths from "constants/appPaths";
import type { ToolPartProps } from "./types";

interface OpenSchemaInput {
    schemaId: string;
    schemaName: string;
}

interface OpenSchemaOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
}

interface ExtraProps {
    mode: "view" | "edit";
}

export default function OpenSchemaTool({
    toolCallId,
    state,
    input,
    output,
    errorText,
    helpers,
    mode,
}: ToolPartProps<OpenSchemaInput, OpenSchemaOutput> & ExtraProps) {
    const { history, urlParams, addToolOutput } = helpers;
    const toolName = mode === "edit" ? "editSchema" : "viewSchema";
    const Icon = mode === "edit" ? EditOutlined : EyeOutlined;
    const verb = mode === "edit" ? "Edit" : "View";

    const handleOpen = () => {
        const tenant = (urlParams.tenant || "").toLowerCase();
        const businessarea = (urlParams.businessarea || "").toLowerCase();
        if (!tenant || !businessarea) {
            addToolOutput({ tool: toolName, toolCallId, output: { success: false, error: "Missing tenant or business area context." } });
            return;
        }
        if (!input?.schemaId) {
            addToolOutput({ tool: toolName, toolCallId, output: { success: false, error: "Missing schemaId." } });
            return;
        }
        const path = AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", tenant)
            .replace(":businessarea", businessarea)
            .replace(":schemaId", input.schemaId)
            .replace(":schemaAction", mode);
        history.push(path);
        addToolOutput({ tool: toolName, toolCallId, output: { success: true } });
    };

    const handleCancel = () => {
        addToolOutput({ tool: toolName, toolCallId, output: { success: false, cancelled: true } });
    };

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-1 flex items-center gap-2 text-[#001D3D]">
                <Icon />
                <span className="text-xs font-semibold uppercase tracking-wider">{verb} schema</span>
            </div>
            <div className="text-base font-semibold text-zinc-900">{input?.schemaName || input?.schemaId}</div>
            <div className="text-[10px] text-zinc-500">id: {input?.schemaId}</div>

            {state === "output-available" && output?.success && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Opened.
                </div>
            )}
            {state === "output-available" && !output?.success && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-available" && !output?.success && !output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {output?.error || "Open failed."}
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {(state === "input-streaming" || state === "input-available") && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={state === "input-streaming"}
                        className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleOpen}
                        disabled={state === "input-streaming"}
                        className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Open
                    </button>
                </div>
            )}
        </div>
    );
}
